import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { applierGetJobs } from "../../api/jobs";
import JobCard from "../../components/ui/ui/JobCard";
import FilterPanel from "../../components/ui/ui/FilterPanel";
import {
 Loader2,
 Search,
 ChevronLeft,
 ChevronRight,
 Filter,
 X,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
 Dialog,
 DialogContent,
 DialogHeader,
 DialogTitle,
 DialogTrigger,
} from "../../components/ui/dialog";
import Loader from "../../components/ui/Loader";
import AnimatedBackground from "../../components/ui/AnimatedBackground";



const JobSearch = () => {
 const [searchParams] = useSearchParams();
 const companyParam = searchParams.get("company");

 const [jobs, setJobs] = useState([]);
 const [filters, setFilters] = useState(companyParam ? { company: companyParam } : {});
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");
 const [pagination, setPagination] = useState({});
 const [currentPage, setCurrentPage] = useState(1);
 const [searchQuery, setSearchQuery] = useState("");
 const [filterDialogOpen, setFilterDialogOpen] = useState(false);

 const fetchJobs = async (f = {}, page = 1) => {
 try {
 setLoading(true);
 setError("");
 const params = { ...f, page, limit: 12 }; // Adjust limit as needed
 const res = await applierGetJobs(params);
 const filteredJobs = (res.data.jobs || []).filter((job) => {
 const isExpired = job.status === "EXPIRED" || (job.deadline && new Date(job.deadline) < new Date());
 return !isExpired;
 });
 setJobs(filteredJobs);
 setPagination(res.data.pagination || {});
 } catch (err) {
 console.error("Failed to fetch jobs:", err);
 setJobs([]);
 setError("Failed to load jobs. Please try again.");
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchJobs(filters, currentPage);
 }, [currentPage, filters]); // Added filters to dependencies to refetch when filters change (like from URL)

 useEffect(() => {
 if (companyParam) {
 setFilters((prev) => ({ ...prev, company: companyParam }));
 setCurrentPage(1);
 }
 }, [companyParam]);

 const handleFilterChange = (newFilters) => {
 setFilters(newFilters);
 setCurrentPage(1); // Reset to first page on filter change
 fetchJobs(newFilters, 1);
 setFilterDialogOpen(false); // Close dialog on mobile after applying
 };

 const handleSearch = () => {
 const newFilters = { ...filters, q: searchQuery };
 handleFilterChange(newFilters);
 };

 const handlePageChange = (page) => {
 setCurrentPage(page);
 };

 return (
 <div className="min-h-screen relative bg-transparent">
 <AnimatedBackground />
 <div className="max-w-7xl mx-auto py-8 px-4">
 {/* Header */}
 <div className="mb-8">
 <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
 Find Your Next Job
 </h1>
 <p className="text-base sm:text-lg text-muted-foreground">
 Discover opportunities that match your skills and interests
 </p>
 </div>

 {/* Search Bar */}
 <div className="flex gap-2 mb-8 max-w-2xl">
 <Input
 type="text"
 placeholder="Search jobs by title, company..."
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 onKeyPress={(e) => e.key === "Enter" && handleSearch()}
 className="h-11"
 />
 <Button
 onClick={handleSearch}
 className="gap-2 px-6"
 >
 <Search className="w-4 h-4" />
 <span className="hidden sm:inline">Search</span>
 </Button>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
 {/* Filter Panel - Desktop */}
 <div className="hidden lg:block lg:col-span-1">
 <FilterPanel onFilter={handleFilterChange} />
 </div>

 {/* Mobile Filter Button */}
 <div className="lg:hidden mb-4">
 <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
 <DialogTrigger asChild>
 <Button variant="outline" className="w-full sm:w-auto gap-2">
 <Filter className="w-4 h-4" />
 Filters & Sort
 </Button>
 </DialogTrigger>
 <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Filters</DialogTitle>
 </DialogHeader>
 <FilterPanel onFilter={handleFilterChange} />
 </DialogContent>
 </Dialog>
 </div>

 {/* Job Listings */}
 <div className="lg:col-span-3">
 {loading ? (
 <Loader />
 ) : error ? (
 <div className="text-center py-20">
 <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
 <X className="w-8 h-8 text-destructive" />
 </div>
 <p className="text-lg font-semibold text-foreground mb-2">
 {error}
 </p>
 <Button
 onClick={() => fetchJobs(filters, currentPage)}
 className="mt-4"
 >
 Try Again
 </Button>
 </div>
 ) : jobs.length === 0 ? (
 <div className="text-center py-20">
 <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
 <Search className="w-8 h-8 text-muted-foreground" />
 </div>
 <p className="text-lg font-semibold text-foreground mb-2">
 No jobs found
 </p>
 <p className="text-sm text-muted-foreground">
 Try adjusting your filters or search query
 </p>
 </div>
 ) : (
 <>
 <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
 {jobs.map((job) => (
 <JobCard key={job.id} job={job} />
 ))}
 </div>

 {/* Pagination */}
 {pagination.pages > 1 && (
 <div className="flex justify-center items-center gap-2 mt-8">
 <Button
 variant="outline"
 size="sm"
 onClick={() => handlePageChange(currentPage - 1)}
 disabled={currentPage === 1}
 className="gap-1"
 >
 <ChevronLeft className="w-4 h-4" />
 Previous
 </Button>
 <span className="text-sm text-muted-foreground px-4">
 Page {currentPage} of {pagination.pages}
 </span>
 <Button
 variant="outline"
 size="sm"
 onClick={() => handlePageChange(currentPage + 1)}
 disabled={currentPage === pagination.pages}
 className="gap-1"
 >
 Next
 <ChevronRight className="w-4 h-4" />
 </Button>
 </div>
 )}
 </>
 )}
 </div>
 </div>
 </div>
 </div>
 );
};

export default JobSearch;
